import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  FormControlLabel, Switch, Select, MenuItem, FormControl,
  InputLabel, FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const ACC_HEAD_TYPES = [
  { value: 1, label: 'Income / Expense' },
  { value: 2, label: 'Assets' },
  { value: 3, label: 'Others' },
];

const AccHeadModal = ({ open, onClose, onSave, AccHeadData = null, mode = 'create', existingAccHeads = [] }) => {
  const { companyId, userId, token, roleName } = useAuth();
  const isAdminRole = roleName === 'CompanyAdmin' || roleName === 'SuperAdmin';
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [formData, setFormData] = useState({
    AccHeadType: '',
    Name: '',
    OpeningBalance: '',
    IsPayable: false,
    IsExpense: false,
    IsActive: true,
    IncludeInPL: false,
    IsAdminView: false,
    Depreciation: '',
  });
  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (AccHeadData && mode !== 'create') {
        setFormData({
          Id: AccHeadData.Id || '',
          AccHeadType: AccHeadData.AccHeadType ?? '',
          Name: AccHeadData.Name || '',
          OpeningBalance: AccHeadData.OpeningBalance ?? '',
          IsPayable: AccHeadData.IsPayable ?? false,
          IsExpense: AccHeadData.IsExpense ?? false,
          IsActive: AccHeadData.IsActive ?? true,
          IncludeInPL: AccHeadData.IncludeInPL ?? false,
          IsAdminView: AccHeadData.IsAdminView ?? false,
          Depreciation: AccHeadData.Depreciation ?? '',
        });
      } else {
        setFormData({
          AccHeadType: '',
          Name: '',
          OpeningBalance: '',
          IsPayable: false,
          IsExpense: false,
          IsActive: true,
          IncludeInPL: false,
          IsAdminView: false,
          Depreciation: '',
        });
      }
      setErrors({});
    }
  }, [open, AccHeadData, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.AccHeadType) newErrors.AccHeadType = 'Account Head Type is required';
    if (!formData.Name?.trim()) newErrors.Name = 'Name is required';
    if (formData.OpeningBalance === '' || formData.OpeningBalance === null) {
      newErrors.OpeningBalance = 'Opening Balance is required';
    } else if (isNaN(formData.OpeningBalance)) {
      newErrors.OpeningBalance = 'Enter a valid amount';
    }
    if (formData.Depreciation === '' || formData.Depreciation === null) {
      newErrors.Depreciation = 'Depreciation is required';
    } else if (isNaN(formData.Depreciation) || parseFloat(formData.Depreciation) < 0) {
      newErrors.Depreciation = 'Enter a valid depreciation value';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = () => existingAccHeads.find(a => {
    if (mode === 'edit' && AccHeadData && a.Id === AccHeadData.Id) return false;
    return a.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
  });

  const handleSave = async () => {
    if (!validateForm()) return;
    const duplicate = checkDuplicate();
    if (duplicate) {
      setDuplicateMessage(`An Account Head with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }
    setLoading(true);
    try {
      const dataToSend = {
        AccHeadType: parseInt(formData.AccHeadType),
        Name: formData.Name,
        OpeningBalance: parseFloat(formData.OpeningBalance) || 0,
        IsPayable: formData.IsPayable,
        IsExpense: formData.IsExpense,
        IsActive: formData.IsActive,
        IncludeInPL: formData.IncludeInPL,
        IsAdminView: formData.IsAdminView,
        Depreciation: parseFloat(formData.Depreciation) || 0,
        CompanyId: companyId,
      };
      if (mode === 'edit') {
        if (!AccHeadData || !AccHeadData.Id) { alert('Error: Cannot update Account Head - missing ID'); return; }
        dataToSend.Id = AccHeadData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        if (AccHeadData.CreatedBy) dataToSend.CreatedBy = AccHeadData.CreatedBy;
        if (AccHeadData.CreatedDate) dataToSend.CreatedDate = AccHeadData.CreatedDate;
        await updateData('AccHeadApi', AccHeadData.Id, dataToSend, token);
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        await postData('AccHeadApi','', dataToSend, token);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving Account Head:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => ({ create: 'New Account Head', edit: 'Edit Account Head', view: 'Account Head Details' }[mode] || 'Account Head');
  const isReadOnly = mode === 'view';
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            padding: '2px', margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{ background: 'white', borderRadius: isMobile ? '0' : '10px', overflow: 'hidden', height: isMobile ? '100vh' : 'auto' }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            color: 'white', padding: '8px 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AccountBalanceIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>{getTitle()}</Typography>
            </Box>
            <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px' }}>
            <Typography variant="subtitle2" style={{ color: '#666', marginBottom: '6px', fontSize: '10px', lineHeight: '1.2' }}>
              {mode === 'create' ? 'Add a new Account Head to your system' : mode === 'edit' ? 'Update Account Head information' : 'View Account Head details'}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px' }}>
                  Account Head Information
                </Typography>
              </Grid>

              {/* Name - Full Width */}
              <Grid item xs={12}>
                <TextField
                  name="Name" label="Name *" value={formData.Name} onChange={handleChange}
                  fullWidth size="small" error={!!errors.Name} helperText={errors.Name}
                  disabled={isReadOnly} placeholder="Enter account head name" inputProps={{ maxLength: 50 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* AccHeadType */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" error={!!errors.AccHeadType} disabled={isReadOnly}>
                  <InputLabel style={labelStyle}>Account Head Type *</InputLabel>
                  <Select
                    name="AccHeadType"
                    value={formData.AccHeadType}
                    onChange={handleChange}
                    label="Account Head Type *"
                    style={{ ...inputStyle, height: '32px' }}
                  >
                    {ACC_HEAD_TYPES.map(t => (
                      <MenuItem key={t.value} value={t.value} style={{ fontSize: '13px' }}>{t.label}</MenuItem>
                    ))}
                  </Select>
                  {errors.AccHeadType && <FormHelperText style={helperStyle}>{errors.AccHeadType}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Opening Balance */}
              <Grid item xs={12} sm={4}>
                <TextField
                  name="OpeningBalance" label="Opening Balance *" value={formData.OpeningBalance} onChange={handleChange}
                  fullWidth size="small" type="number" error={!!errors.OpeningBalance} helperText={errors.OpeningBalance}
                  disabled={isReadOnly} placeholder="0.00" inputProps={{ step: '0.01' }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Depreciation */}
              <Grid item xs={12} sm={4}>
                <TextField
                  name="Depreciation" label="Depreciation *" value={formData.Depreciation} onChange={handleChange}
                  fullWidth size="small" type="number" error={!!errors.Depreciation} helperText={errors.Depreciation}
                  disabled={isReadOnly} placeholder="0.00" inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Toggle switches */}
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Switch checked={formData.IsPayable} onChange={handleChange} name="IsPayable" disabled={isReadOnly} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                  label={<Typography style={{ fontSize: '11px' }}>Is Payable</Typography>}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Switch checked={formData.IsExpense} onChange={handleChange} name="IsExpense" disabled={isReadOnly} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                  label={<Typography style={{ fontSize: '11px' }}>Is Expense</Typography>}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Switch checked={formData.IncludeInPL} onChange={handleChange} name="IncludeInPL" disabled={isReadOnly} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                  label={<Typography style={{ fontSize: '11px' }}>Include in P&L</Typography>}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Switch checked={formData.IsActive} onChange={handleChange} name="IsActive" disabled={isReadOnly} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                  label={<Typography style={{ fontSize: '11px' }}>Is Active</Typography>}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Switch checked={formData.IsAdminView} onChange={handleChange} name="IsAdminView" disabled={isReadOnly || !isAdminRole} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                  label={<Typography style={{ fontSize: '11px' }}>Is Admin View</Typography>}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions style={{ padding: '8px 12px', background: '#f8f9fa', borderTop: '1px solid #e9ecef', gap: '6px', minHeight: 'auto' }}>
            <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}>
              Cancel
            </Button>
            {!isReadOnly && (
              <Button onClick={handleSave} disabled={loading} variant="contained"
                style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #9a7a2e 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 12px', fontSize: '12px' }}>
                {loading ? 'Saving...' : mode === 'create' ? 'Add Account Head' : 'Update Account Head'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px' }}>
            Duplicate Account Head Found
          </Typography>
          <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>{duplicateMessage}</Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button onClick={() => setDuplicateDialogOpen(false)} variant="contained"
            style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #7a5230 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '8px 32px', fontSize: '14px' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccHeadModal;
